// src/mobile/detail-navigation.ts
import {
  EventManager,
  removeEventAllListeners,
  setupEventListenersIfNeeded,
  WILDCARD_ACTION_ID,
} from './event-manager';
import {
  DetailNavigationBarConfig,
  NavigationItem,
  MoreMenuGroup,
  NavigationEventType,
} from './types';
import { postJsMessage } from '../../../bridge';
import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';
import API from '../../../api';
import { isAvailable } from '../../utils';
import { PostMessageOption } from '../../../types';

function processMenuItems(items: NavigationItem[]): NavigationItem[] {
  return items.map(({ onTap, ...item }) => {
    if (item.actionID && onTap) {
      const eventType: NavigationEventType = 'onDetailMoreMenuClick';
      const actionID = item.actionID;
      EventManager.getInstance().replaceCallback(eventType, actionID, onTap);
    }
    return item;
  });
}

function processMenuGroups(groups: MoreMenuGroup[]): MoreMenuGroup[] {
  return groups.map((group) => ({
    ...group,
    items: processMenuItems(group.items),
  }));
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.setDetailNavigationBarConfig
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0
 * android    6.5.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function setDetailNavigationBarConfig(
  config: DetailNavigationBarConfig,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<unknown>> {
  await isAvailable('mobile.setDetailNavigationBarConfig');
  setupEventListenersIfNeeded();

  const processedConfig = { ...config };

  if (processedConfig.backButton?.onTap) {
    const eventType: NavigationEventType = 'onBackButtonClick';
    EventManager.getInstance().replaceCallback(
      eventType,
      WILDCARD_ACTION_ID,
      processedConfig.backButton.onTap
    );
    delete processedConfig.backButton.onTap;
  }

  if (processedConfig.onDetailMoreMenuClick) {
    EventManager.getInstance().replaceCallback(
      'onDetailMoreMenuClick',
      WILDCARD_ACTION_ID,
      processedConfig.onDetailMoreMenuClick
    );
    delete processedConfig.onDetailMoreMenuClick;
  }

  if (processedConfig.moreMenuContent?.itemsGroup) {
    processedConfig.moreMenuContent.itemsGroup = processMenuGroups(
      processedConfig.moreMenuContent.itemsGroup
    );
  }

  if (processedConfig.navBarRightItems) {
    processedConfig.navBarRightItems = processedConfig.navBarRightItems.map(
      ({ onTap, ...item }) => {
        if (item.actionID && onTap) {
          const eventType: NavigationEventType = 'onNavRightItemClick';
          EventManager.getInstance().replaceCallback(
            eventType,
            item.actionID,
            onTap
          );
        }
        return {
          ...item,
          useIconAsTemplate: item.useIconAsTemplate ?? true,
        };
      }
    );
  }

  return postJsMessage(
    API.setDetailNavigationBarConfig,
    processedConfig,
    options
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.removeDetailNavigationBarListeners
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0
 * android    6.5.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function removeDetailNavigationBarListeners() {
  await isAvailable('mobile.removeDetailNavigationBarListeners');
  setupEventListenersIfNeeded();

  await removeEventAllListeners('onDetailMoreMenuClick');
  await removeEventAllListeners('onNavRightItemClick');
  await removeEventAllListeners('onBackButtonClick');
}
